import { Suspense, useEffect } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconTimesDefault } from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { Button } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { useGetDrawdownActionPanelData } from './useGetDrawdownActionPanelData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import CustomiseOffer from '../../../myOffers/approvedView/rblApprovedView/CustomiseOffer';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/MoneyWithDecimals';
import { Rate } from '../../../ui/rate/Rate';
import { DrawdownChoice } from '../../OutstandingView/dashboardActions/TopUpPanel/DrawdownChoice';

export const DrawdownActionPanel = ({
    accountId,
    setDrawdownPanelIsOpen
}: {
    accountId: NonNullable<TAccount['account_id']>;
    setDrawdownPanelIsOpen: (drawdownPanelIsOpen: boolean) => void;
}) => {
    const { isRBL } = useGetDrawdownActionPanelData(accountId);

    useEffect(() => {
        tracking.ampli.drawdownPanelViewed({
            account_id: accountId
        });
    }, [accountId]);

    return (
        <div className="rounded-b-l border border-secondary-90 bg-primary-97 px-xl pb-xl">
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense
                    fallback={
                        <div
                            className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                            data-testId="loan-overview-loading"
                        >
                            <MainContentPending />
                        </div>
                    }
                >
                    <div className="flex flex-row items-center justify-between">
                        <Text className="text-2xl font-bold">
                            <FormattedMessage
                                id="_.drawdown_action_panel.topup_approved.title"
                                defaultMessage="Drawdown funds"
                            />
                        </Text>
                        <Button
                            variant="tertiary"
                            className="mr-[-15px]"
                            onClick={() => setDrawdownPanelIsOpen(false)}
                            data-testid="close-drawdown-panel"
                        >
                            <IconTimesDefault className="h-xl w-xl" />
                        </Button>
                    </div>
                    {isRBL ? (
                        <CustomiseOffer accountId={accountId} />
                    ) : (
                        <>
                            <ApprovalSummaryHeader accountId={accountId} />
                            <DrawdownChoice
                                accountId={accountId}
                                hideHeader={true}
                            />
                        </>
                    )}
                </Suspense>
            </ErrorBoundary>
        </div>
    );
};

const ApprovalSummaryHeader = ({ accountId }: { accountId: string }) => {
    const { topUpStatus, topupInterestRate, creditLimit } =
        useGetDrawdownActionPanelData(accountId);

    switch (topUpStatus) {
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
            return (
                <FormattedMessage
                    id="_.drawdown_action_panel.interestOnlyExtensionSummary.title"
                    defaultMessage="You can extend your interest-only period by 3 months keeping your interest rate at {interestRate} per month"
                    values={{
                        interestRate: (
                            <span>
                                <Rate value={topupInterestRate!} />
                            </span>
                        )
                    }}
                />
            );
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            return (
                <FormattedMessage
                    id="_.drawdown_action_panel.interestOnlyExtensionWithFundsSummary.title"
                    defaultMessage="You can extend your interest-only period by 3 months and draw down up to {amount} with an interest rate of {interestRate} per month"
                    values={{
                        amount: (
                            <span>
                                <MoneyWithDecimals value={creditLimit} />
                            </span>
                        ),
                        interestRate: (
                            <span>
                                <Rate value={topupInterestRate!} />
                            </span>
                        )
                    }}
                />
            );
        default:
            return (
                <Text className="text-m font-reg">
                    <FormattedMessage
                        id="_.drawdown_action_panel.topUpApprovalSummary.title"
                        defaultMessage="You can draw down up to {amount} with an interest rate of {interestRate} per month."
                        values={{
                            amount: (
                                <span>
                                    <MoneyWithDecimals value={creditLimit} />
                                </span>
                            ),
                            interestRate: (
                                <span>
                                    <Rate value={topupInterestRate!} />
                                </span>
                            )
                        }}
                    />
                </Text>
            );
    }
};
